import { useState, useEffect, useRef, useCallback } from "react";

const MAX_RECONNECT_ATTEMPTS = 10;
const BASE_RECONNECT_DELAY_MS = 1000;

/**
 * WebSocket 연결을 관리하는 커스텀 훅
 * @param {string} url - WebSocket URL
 * @param {() => Promise<string>} getToken - 호출 시 fresh WebSocket 토큰을 반환하는 비동기 함수
 * @param {Object} options - 추가 옵션들
 */
const useWebSocket = (url, getToken, options = {}) => {
    const { onMessage = () => {}, onOpen = () => {}, onClose = () => {}, onError = () => {}, enabled = true } = options;

    const [connectionStatus, setConnectionStatus] = useState("Disconnected");
    const [lastMessage, setLastMessage] = useState(null);
    const [error, setError] = useState(null);

    const ws = useRef(null);
    const isConnectingRef = useRef(false);
    const connectionAttemptRef = useRef(0);
    const lastConnectionUrlRef = useRef(null);

    // 하트비트 타이머 참조
    const heartbeatIntervalRef = useRef(null);

    // 자동 재연결 관련
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptRef = useRef(0);
    // 탭이 숨겨져서 재연결을 보류한 상태인지 표시 (visible 전환 시 즉시 재시도)
    const pendingReconnectRef = useRef(false);

    // connect 함수 ref (scheduleReconnect ↔ connect 순환 의존 회피)
    const connectRef = useRef(null);

    // 메시지 전송 함수
    const sendMessage = useCallback((message) => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            try {
                const messageString = typeof message === "string" ? message : JSON.stringify(message);
                ws.current.send(messageString);
                console.log("Message sent:", messageString);
                return true;
            } catch (err) {
                console.error("Failed to send message:", err);
                setError("메시지 전송 실패");
                return false;
            }
        } else {
            console.warn("WebSocket is not connected");
            setError("WebSocket이 연결되지 않았습니다");
            return false;
        }
    }, []);

    /**
     * 하트비트 메시지를 2분마다 전송하는 함수
     */
    const startHeartbeat = useCallback(() => {
        // 기존 타이머 정리
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
        }

        console.log("Starting heartbeat (every 2 minutes)...");

        // 2분(120초)마다 하트비트 전송
        heartbeatIntervalRef.current = setInterval(
            () => {
                if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                    console.log("Sending heartbeat...");
                    sendMessage({
                        action: "sendHeartBeat",
                        data: { status: "UP" },
                    });
                }
            },
            2 * 60 * 1000,
        ); // 2분
    }, [sendMessage]);

    /**
     * 하트비트 정지
     */
    const stopHeartbeat = useCallback(() => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
            console.log("Heartbeat stopped");
        }
    }, []);

    // 재연결 시도 취소 함수
    const cancelReconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        reconnectAttemptRef.current = 0;
        pendingReconnectRef.current = false;
    }, []);

    // 자동 재연결 스케줄링 (지수 백오프)
    const scheduleReconnect = useCallback(() => {
        if (!enabled || !url) return;

        // 탭이 숨겨져 있거나 절전모드면 재연결 요청을 보류 (보일 때 재개)
        if (typeof document !== "undefined" && document.visibilityState === "hidden") {
            pendingReconnectRef.current = true;
            return;
        }

        if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
            setError("자동 재연결 실패: 최대 시도 횟수 초과");
            reconnectAttemptRef.current = 0;
            return;
        }

        reconnectAttemptRef.current += 1;
        const delay = BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttemptRef.current - 1);
        reconnectTimeoutRef.current = setTimeout(() => {
            connectRef.current?.();
        }, delay);
    }, [enabled, url]);

    // WebSocket 연결 함수
    const connect = useCallback(async () => {
        if (!url || !getToken || !enabled) {
            console.log("Connect cancelled: missing parameters", { url: !!url, enabled });
            return;
        }

        if (isConnectingRef.current) {
            console.log("Connect cancelled: already connecting");
            return;
        }

        if (ws.current && ws.current.readyState === WebSocket.OPEN && lastConnectionUrlRef.current === url) {
            console.log("Connect cancelled: already connected to same URL");
            return;
        }

        const attemptNumber = ++connectionAttemptRef.current;
        isConnectingRef.current = true;
        setConnectionStatus("Connecting");
        setError(null);

        // fresh token 획득
        let token;
        try {
            console.log(`Starting connection attempt #${attemptNumber} - fetching token...`);
            token = await getToken();
        } catch (err) {
            console.error(`🔑 Token fetch failed for attempt #${attemptNumber}:`, err);
            isConnectingRef.current = false;
            setError(`토큰 발급 실패: ${err.message}`);
            setConnectionStatus("Error");
            scheduleReconnect();
            return;
        }

        // await 동안 disconnect/언마운트 등이 발생했는지 확인
        if (connectionAttemptRef.current !== attemptNumber) {
            console.log(`Connection attempt #${attemptNumber} aborted: newer attempt or disconnect in progress`);
            isConnectingRef.current = false;
            return;
        }

        if (!token) {
            console.error(`🔑 Token fetch returned empty for attempt #${attemptNumber}`);
            isConnectingRef.current = false;
            setError("토큰 발급 실패: 빈 토큰");
            setConnectionStatus("Error");
            scheduleReconnect();
            return;
        }

        if (ws.current) {
            console.log("Closing existing connection");
            ws.current.close();
            ws.current = null;
        }

        try {
            lastConnectionUrlRef.current = url;

            console.log(`Creating WebSocket connection #${attemptNumber}`);

            // WebSocket 토큰을 쿼리 파라미터로 전달
            const finalUrl = `${url}?authorization=${encodeURIComponent(token)}`;
            console.log("🔗 WebSocket Connection Debug:", {
                baseUrl: url,
                tokenLength: token.length,
            });

            ws.current = new WebSocket(finalUrl);

            ws.current.onopen = (event) => {
                if (connectionAttemptRef.current !== attemptNumber) {
                    console.log(`Connection #${attemptNumber} ignored: newer attempt in progress`);
                    return;
                }

                console.log(`✅ WebSocket 연결됨 (#${attemptNumber})`);

                // 재연결 성공 시 재연결 시도 횟수 리셋
                reconnectAttemptRef.current = 0;
                cancelReconnect();

                // 하트비트 시작
                startHeartbeat();

                isConnectingRef.current = false;
                setConnectionStatus("Connected");
                setError(null);
                lastConnectionUrlRef.current = url;
                onOpen(event);
            };

            ws.current.onmessage = (event) => {
                try {
                    const response = JSON.parse(event.data);
                    console.log("WebSocket message received:", response);

                    // 응답 구조: { message: "success", data: {...}, function: "..." }
                    const { action: functionName, data } = response;

                    // 각 함수별 처리
                    if (functionName === "fetchMessages") {
                        // 메시지 목록 조회 응답
                        console.log("Received message list:", data.results.length, "messages");
                        onMessage({
                            type: "MESSAGE_LIST",
                            data: data.results,
                            nextToken: data.nextToken,
                        });
                    } else if (functionName === "putMessageStatus") {
                        // 메시지 상태 변경 응답
                        console.log("Message status updated successfully");
                        onMessage({
                            type: "STATUS_UPDATED",
                        });
                    } else if (functionName === "sendHeartBeat") {
                        // 하트비트 응답 (무시)
                        console.log("Heartbeat acknowledged");
                    } else if (functionName === "notify") {
                        // 📢 실시간 notify 알림
                        console.log("📢 Received NOTIFY:", data);
                        onMessage({
                            type: "NOTIFY",
                            data: data,
                        });
                    } else if (functionName === "sessionChatReply") {
                        // AI 분석 Chat 응답
                        onMessage({
                            type: "SESSION_CHAT_REPLY",
                            data: data,
                        });
                    } else if (functionName === "aiCaptionJobStatusChanged") {
                        // AI Caption 작업 상태 변경 알림
                        console.log("📢 Received AI Caption job status changed:", data);
                        onMessage({
                            type: "AI_CAPTION_JOB_STATUS_CHANGED",
                            data: data,
                        });
                    } else {
                        // 예상치 못한 응답
                        console.log("Received unexpected message:", functionName);
                        setLastMessage(response);
                    }
                } catch (err) {
                    console.error("Failed to parse WebSocket message:", err, "Raw data:", event.data);
                    const rawMessage = {
                        raw: event.data,
                        timestamp: new Date().toISOString(),
                        type: "RAW",
                    };
                    setLastMessage(rawMessage);
                }
            };

            ws.current.onclose = (event) => {
                if (connectionAttemptRef.current !== attemptNumber) {
                    console.log(`Connection close #${attemptNumber} ignored: newer attempt in progress`);
                    return;
                }

                const timestamp = new Date().toISOString();
                const closeInfo = {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean,
                    timestamp,
                };

                console.warn(`🔌 WebSocket 연결 끊김 (#${attemptNumber}, code=${event.code})`, closeInfo);

                // 하트비트 정지
                stopHeartbeat();

                const getCloseReason = (code) => {
                    const reasons = {
                        1001: "Going away",
                        1006: "Abnormal closure - 서버 연결 거부 또는 네트워크 오류",
                    };
                    return reasons[code] || `Unknown error (${code})`;
                };

                const closeReason = getCloseReason(event.code);
                console.error(`❌ Close reason: ${closeReason}`);

                // 에러 메시지 설정 (사용자에게 표시)
                let errorMessage = null;
                if (event.code === 1006) {
                    errorMessage =
                        "❌ 연결 실패 (Code 1006)\n서버가 연결을 거부했습니다.\n\n확인 사항:\n1. WebSocket URL이 정확한가?\n2. 토큰이 유효한가?\n3. API Gateway 설정이 올바른가?";
                }

                if (errorMessage) {
                    console.error("📢 Error message:", errorMessage);
                    setError(errorMessage);
                }

                isConnectingRef.current = false;
                setConnectionStatus("Disconnected");
                ws.current = null;
                lastConnectionUrlRef.current = null;
                onClose(event);

                // 비정상 종료인 경우 자동 재연결 시도
                // code 1000 (정상 종료) 또는 1001 (Going away)는 재연결하지 않음
                if (enabled && url && event.code !== 1000) {
                    scheduleReconnect();
                } else {
                    // 정상 종료인 경우 재연결 시도 횟수 리셋
                    reconnectAttemptRef.current = 0;
                }
            };

            ws.current.onerror = (event) => {
                if (connectionAttemptRef.current !== attemptNumber) {
                    console.log(`Connection error #${attemptNumber} ignored: newer attempt in progress`);
                    return;
                }

                console.error(`WebSocket error #${attemptNumber}:`, event);
                isConnectingRef.current = false;
                setError("연결 오류 - 네트워크 또는 서버 설정을 확인해주세요");
                setConnectionStatus("Error");
                lastConnectionUrlRef.current = null;
                onError(event);
            };
        } catch (err) {
            console.error(`Failed to create WebSocket connection #${attemptNumber}:`, err);
            isConnectingRef.current = false;
            setError(`연결 생성 실패: ${err.message}`);
            setConnectionStatus("Error");
            lastConnectionUrlRef.current = null;
        }
    }, [
        url,
        getToken,
        enabled,
        onMessage,
        onOpen,
        onClose,
        onError,
        startHeartbeat,
        stopHeartbeat,
        cancelReconnect,
        scheduleReconnect,
    ]);

    // connect 최신 참조를 ref에 저장 (scheduleReconnect의 setTimeout이 항상 최신 connect 호출)
    useEffect(() => {
        connectRef.current = connect;
    }, [connect]);

    // WebSocket 연결 해제 함수
    const disconnect = useCallback(() => {
        console.log("Manual disconnect requested");
        isConnectingRef.current = false;
        lastConnectionUrlRef.current = null;
        // 진행 중인 attempt를 무효화 (await 후 재진입 방지)
        connectionAttemptRef.current += 1;

        // 재연결 시도 취소
        cancelReconnect();

        // 하트비트 정지
        stopHeartbeat();

        if (ws.current) {
            ws.current.close(1000, "Manual disconnect");
            ws.current = null;
        }

        setConnectionStatus("Disconnected");
    }, [stopHeartbeat, cancelReconnect]);

    // 수동 재연결 함수
    const reconnect = useCallback(() => {
        disconnect();
        setTimeout(() => {
            connect();
        }, 1000);
    }, [disconnect, connect]);

    // 컴포넌트 마운트/언마운트 및 의존성 변경 처리
    useEffect(() => {
        if (enabled && url && getToken) {
            const timeoutId = setTimeout(() => {
                connect();
            }, 100);

            return () => {
                clearTimeout(timeoutId);
                // 클린업 시에는 disconnect 호출하지 않음 (의존성 변경 시 재연결 시도 방지)
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, getToken, enabled]);

    // 컴포넌트 언마운트 시 하트비트 정지 및 재연결 취소
    useEffect(() => {
        return () => {
            stopHeartbeat();
            cancelReconnect();
        };
    }, [stopHeartbeat, cancelReconnect]);

    // 네트워크 상태 감지 및 자동 재연결
    useEffect(() => {
        const handleOnline = () => {
            // 네트워크가 다시 연결되었을 때, WebSocket이 연결되어 있지 않으면 재연결 시도
            if (enabled && url && getToken && (!ws.current || ws.current.readyState !== WebSocket.OPEN)) {
                // 탭이 숨겨진 상태에서는 즉시 시도하지 않고 보류 — visibilitychange에서 재개
                if (typeof document !== "undefined" && document.visibilityState === "hidden") {
                    pendingReconnectRef.current = true;
                    return;
                }
                reconnectAttemptRef.current = 0; // 재연결 시도 횟수 리셋
                // 네트워크 복구 시 즉시 재연결 시도
                if (!isConnectingRef.current) {
                    connect();
                }
            }
        };

        const handleOffline = () => {};

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, [enabled, url, getToken, connect]);

    // 페이지 가시성(Page Visibility) 기반 재연결 제어
    useEffect(() => {
        if (!enabled || !url) return undefined;

        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                // 탭 숨김 — 진행 중인 재연결 타이머는 취소하고 보류 상태로 전환
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                    pendingReconnectRef.current = true;
                }
                return;
            }

            // visible 전환 — 보류된 재연결이 있거나 연결이 끊긴 상태면 즉시 재시도
            const isOpen = ws.current && ws.current.readyState === WebSocket.OPEN;
            if (isOpen || isConnectingRef.current) return;
            if (!pendingReconnectRef.current) return;

            pendingReconnectRef.current = false;
            reconnectAttemptRef.current = 0;
            connectRef.current?.();
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [enabled, url]);

    return {
        connectionStatus,
        lastMessage,
        error,
        sendMessage,
        reconnect,
        disconnect,
        isConnected: connectionStatus === "Connected",
        isConnecting: connectionStatus === "Connecting",
    };
};

export default useWebSocket;
